import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useId } from "react";

import { DrawerBalancesView } from "~/components/reports/cash-operations/drawer-balances-view";
import { SalesProjectionsView } from "~/components/reports/cash-operations/sales-projections-view";
import { SalesSummaryView } from "~/components/reports/cash-operations/sales-summary-view";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";
import {
  useSearchParamsDateRange,
  requireDateRange,
} from "~/utils/search-params-date-range";

export const Route = createFileRoute("/_authenticated/reports/cash-operations")(
  {
    component: CashOperationsReport,
  }
);

const CASH_OPS_GROUP_BY = [
  "drawer-balances",
  "sales-projections",
  "sales-summary",
] as const;
const isCashOpsGroupBy = enumIncludes(CASH_OPS_GROUP_BY);

function CashOperationsReport() {
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;
  const groupBy = parseEnumValue(
    searchParams.groupBy,
    isCashOpsGroupBy,
    "drawer-balances"
  );
  const [dateRange] = useSearchParamsDateRange();
  const tableId = useId();

  if (!locationId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Location Required</h3>
          <p className="body text-muted-foreground">
            Please select a location from the filters above to view Cash
            Operations.
          </p>
        </div>
      </div>
    );
  }

  if (!(dateRange.from && dateRange.to)) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Date Range Required</h3>
          <p className="body text-muted-foreground">
            Please select a date range from the filters above.
          </p>
        </div>
      </div>
    );
  }

  const typedDateRange = requireDateRange(dateRange);
  if (!typedDateRange) {
    return null;
  }

  const getTitle = () => {
    switch (groupBy) {
      case "drawer-balances":
        return "Cash Drawer Balances";
      case "sales-projections":
        return "Sales Projections";
      case "sales-summary":
        return "Sales Summary";
      default:
        return "Cash Operations";
    }
  };

  const getDescription = () => {
    switch (groupBy) {
      case "drawer-balances":
        return "Daily cash drawer reconciliation and balance tracking";
      case "sales-projections":
        return "Financial forecasting and sales projections";
      case "sales-summary":
        return "Comprehensive sales totals, location breakdown, and category analysis";
      default:
        return "Cash operations and financial data";
    }
  };

  const renderView = () => {
    switch (groupBy) {
      case "drawer-balances":
        return (
          <DrawerBalancesView
            dateRange={typedDateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "sales-projections":
        return (
          <SalesProjectionsView
            dateRange={typedDateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      case "sales-summary":
        return (
          <SalesSummaryView
            dateRange={typedDateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
      default:
        return (
          <DrawerBalancesView
            dateRange={typedDateRange}
            locationId={locationId}
            tableId={tableId}
          />
        );
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">{getTitle()}</h2>
        <p className="body text-muted-foreground">{getDescription()}</p>
      </div>

      {renderView()}
    </div>
  );
}
